import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getPackages, postPayment } from "../services/assuranceService";

export default function InvestmentValidation() {
  const [price, setPrice] = useState(0);
  const [amountDue, setAmountDue] = useState(0);
  const [subscriberData, setSubscriberData] = useState(null);
  const [principalData, setPrincipalData] = useState(null);
  const [isPrincipal, setIsPrincipal] = useState(null);
  const [spouse, setSpouse] = useState(null);
  const [children, setChildren] = useState([]);
  const [periodicity, setPeriodicity] = useState(null);
  const [effectiveDate, setEffectiveDate] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [packageName, setPackageName] = useState(null);
  const [principalIsOver65, setPrincipalIsOver65] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Calcul de l'âge à partir de la date de naissance
  const calculateAge = (dob) => {
    if (!dob) return null;
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  // Fonction pour formater le prix
  const formatCurrency = (amount) => {
    if (!amount || amount === 0) return "0";
    return new Intl.NumberFormat("fr-FR").format(amount);
  };

  // Fonction utilitaire pour extraire le prix de différentes structures (même que Congratulation.jsx)
  const extractPrice = (data) => {
    // Essayer les chemins les plus courants
    const possiblePaths = [
      "data.data.quotation.total_premium.value",
      "data.data.quotation.total_premium",
      "data.data.quotation.totalPremium",
      "data.data.quotation.price",
      "data.data.quotation.amount",
      "data.data.quotation.premium",
      "data.quotation.total_premium.value",
      "data.quotation.total_premium",
      "data.quotation.totalPremium",
      "data.quotation.price",
      "data.quotation.amount",
      "data.quotation.premium",
      "data.quotation",
      "quotation.total_premium.value",
      "quotation.total_premium",
      "quotation.totalPremium",
      "quotation.price",
      "quotation.amount",
      "quotation.premium",
      "result.data.quotation.total_premium",
      "result.quotation.total_premium",
      "response.data.quotation.total_premium",
      "response.quotation.total_premium",
    ];

    for (const path of possiblePaths) {
      const keys = path.split(".");
      let value = data;
      let found = true;

      for (const key of keys) {
        if (value && typeof value === "object" && key in value) {
          value = value[key];
        } else {
          found = false;
          break;
        }
      }

      if (
        found &&
        (typeof value === "number" ||
          (typeof value === "string" && !isNaN(Number(value))) ||
          (typeof value === "object" &&
            value &&
            typeof value.value === "number"))
      ) {
        const price = typeof value === "object" ? Number(value.value) : Number(value);
        // console.log(`Prix trouvé dans: ${path} =`, price);
        return price;
      }
    }

    // Si c'est un nombre directement
    if (typeof data === "number") {
      // console.log("Prix trouvé: data est un nombre =", data);
      return data;
    }

    // Si c'est une chaîne numérique
    if (typeof data === "string" && !isNaN(Number(data))) {
      // console.log("Prix trouvé: data est une chaîne numérique =", data);
      return Number(data);
    }

    // console.log("Aucun prix trouvé dans les chemins testés");
    return null;
  };

  useEffect(() => {
    // console.log("InvestmentValidation useEffect running");

    // Récupérer toutes les données AVANT de vider le localStorage
    try {
      // Données du souscripteur
      const storedSubscriber = localStorage.getItem("subscriberData");
      if (storedSubscriber) {
        setSubscriberData(JSON.parse(storedSubscriber));
      }

      // Données du principal assuré
      const storedPrincipal = localStorage.getItem("principalData");
      if (storedPrincipal) {
        setPrincipalData(JSON.parse(storedPrincipal));
      }

      // Est-ce que le souscripteur est le principal ?
      const storedIsPrincipal = localStorage.getItem("isPrincipal");
      if (storedIsPrincipal) {
        setIsPrincipal(storedIsPrincipal);
      }

      // Conjoint
      const storedSpouse = localStorage.getItem("spouse");
      if (storedSpouse) {
        setSpouse(JSON.parse(storedSpouse));
      }

      // Enfants
      const storedChildren = localStorage.getItem("children");
      if (storedChildren) {
        setChildren(JSON.parse(storedChildren));
      }

      // Périodicité (clé correcte: 'periodicity')
      const storedPeriodicity = localStorage.getItem("periodicity");
      if (storedPeriodicity) {
        setPeriodicity(storedPeriodicity);
      }

      // Date d'effet
      const storedEffectiveDate = localStorage.getItem("effectiveDate");
      if (storedEffectiveDate) {
        setEffectiveDate(storedEffectiveDate);
      }

      // Package sélectionné (clé correcte: 'selectedPackage')
      const storedPackage = localStorage.getItem("selectedPackage");
      if (storedPackage) {
        setSelectedPackage(storedPackage);
        // Récupérer le nom du package depuis l'API
        fetchPackageName(storedPackage);
      }

      // Vérifier si l'assuré principal a plus de 65 ans
      const storedIsPrincipalLocal = localStorage.getItem("isPrincipal");
      const storedSubscriberLocal = JSON.parse(localStorage.getItem("subscriberData") || '{}');
      const storedPrincipalLocal = JSON.parse(localStorage.getItem("principalData") || '{}');
      const principalDob = storedIsPrincipalLocal === 'yes'
        ? storedSubscriberLocal.date_naissance
        : storedPrincipalLocal.date_naissance;
      if (principalDob) {
        const age = calculateAge(principalDob);
        if (age !== null && age > 65) {
          setPrincipalIsOver65(true);
        }
      }
    } catch (error) {
      console.error("Erreur lors de la récupération des données:", error);
    }

    // Récupérer le prix
    const subscribeResult = localStorage.getItem("subscribeResult");
    if (subscribeResult) {
      try {
        const result = JSON.parse(subscribeResult);
        // console.log("Prix récupéré depuis localStorage:", result);

        setAmountDue(result.data.subscription.amount_due);

        // Utiliser la même logique d'extraction que Congratulation.jsx
        // console.log(result)
        const extractedPrice = extractPrice(result);
        if (extractedPrice !== null) {
          // Validation du prix (même logique que Congratulation.jsx)
          const isValidPrice = (price) => {
            return typeof price === "number" && !isNaN(price) && price >= 0;
          };

          if (isValidPrice(extractedPrice)) {
            // console.log("Prix extrait et validé:", extractedPrice);
            setPrice(extractedPrice);
          } else {
            console.error("Prix invalide détecté:", extractedPrice);
          }
        } else {
          // console.log("Aucun prix trouvé dans la réponse");
        }
      } catch (error) {
        console.error("Erreur lors du parsing du résultat de cotation:", error);
      }
    } else {
      // console.log("Aucun résultat de cotation trouvé dans localStorage");
    }

    // NE PAS vider le localStorage ici pour permettre le retour en arrière
    // localStorage.clear() sera appelé après le paiement confirmé

    // console.log("=== DONNÉES RÉCUPÉRÉES POUR RÉCAPITULATIF ===");
  }, []);

  // Ouvrir le lien de paiement
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    const subscribeResult = localStorage.getItem("subscribeResult");
    if (subscribeResult) {
        const result = JSON.parse(subscribeResult);
        let subscription = result.data.subscription;

        const paymentData = {
          amount: subscription.amount_due,
          number: subscription.number,
          token: subscription.token
        };

        try {
          const result = await postPayment(paymentData);

          // Rediriger l'utilisateur sur le lien de paiement
          if (result.status && result.data.payment_url) {
            setIsLoading(false);

            localStorage.setItem('paymentResult', JSON.stringify(result));
            window.location.href = result.data.payment_url;
          }

          setError('Erreur: Réponse API invalide');
          setIsLoading(false);
          return;
        } catch (err) {
          setError('Erreur lors de la souscription: ' + (err.response?.data?.error || err.message));
          setIsLoading(false);
        }
    } else {
      // console.log("Aucun résultat de cotation trouvé dans localStorage");
      setError("Aucun résultat de souscription trouvé dans localStorage");
      setIsLoading(false);
    }
  };

  // Fonction pour récupérer le nom du package
  const fetchPackageName = async (packageKey) => {
    try {
      const packages = await getPackages();
      const packagesList = Array.isArray(packages)
        ? packages
        : packages.data || [];
      const foundPackage = packagesList.find((pkg) => pkg.key === packageKey);
      if (foundPackage) {
        setPackageName(foundPackage.name);
      }
    } catch (error) {
      console.error("Erreur lors de la récupération du nom du package:", error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Colonne gauche : Formulaire */}
        <div className="lg:col-span-2 space-y-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">
              Validation de votre paiement
            </h1>

            {/* Message spécial pour les personnes de plus de 65 ans */}
            {principalIsOver65 ? (
              <div className="mt-4 p-6 bg-amber-50 border border-amber-300 rounded-xl">
                <p className="text-amber-800 text-lg font-semibold">
                  Votre demande sera étudiée par le médecin conseil, car vous avez dépassé l'âge limite de souscription qui est fixé à 65 ans.
                </p>
              </div>
            ) : (
              <>
                <p className="mt-2 text-lg text-secondary">Montant à payer</p>
                <p className="text-2xl font-bold text-red-600">
                  {formatCurrency(amountDue)} F CFA 
                </p>
              </>
            )}
          </div>

          {!principalIsOver65 && (
            <>
              <div className="bg-white rounded-xl shadow-md p-6 gap-4 cursor-pointer hover:shadow-lg transition">
                {/* <div className="flex items-center gap-3"> */}
                  <img
                    src="/assets/images/wave_2.png"
                    alt="Wave"
                    className="h-20 w-auto object-contain rounded-full"
                  />
                {/* </div> */}

                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="payment"
                    className="w-6 h-6 text-primary focus:ring-blue-500" required
                    defaultChecked={true}
                  />
                  Wave
                </label>
              </div>

              <div>
                <p className="text-gray-700 mb-4">
                  Veuillez confirmer les informations de votre assurance santé.
                </p>
                {price === 0 && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                    <p className="text-yellow-800 text-sm mb-2">
                      ⚠️ Le montant de la cotisation n'a pas pu être calculé.
                      Veuillez revenir à l'étape précédente pour recalculer.
                    </p>
                    <button
                      onClick={() => navigate("/step-6")}
                      className="text-blue-600 hover:text-blue-800 text-sm underline"
                    >
                      ← Retour à l'étape 6
                    </button>
                  </div>
                )}
              </div>

              <button
                onClick={handleSubmit}
                disabled={price === 0 || isLoading}
                className={`w-full font-medium py-3 px-6 rounded-lg transition ${
                  price === 0
                    ? "bg-gray-400 cursor-not-allowed text-gray-600"
                    : "bg-secondary hover:bg-secondary-dark text-white"
                }`}
              >
                {isLoading ? 'Paiement en cours...' : 'Confirmer le paiement'}
              </button>
            </>
          )}

          <div className="mt-8 flex flex-col sm:flex-row justify-center text-center gap-4 border-t pt-6">
            <a href="/" target="_blank"  rel="noopener noreferrer"
              className="rounded-full border border-secondary-dark px-5 py-2 text-dark transition hover:bg-secondary-dark hover:text-light">
              Conditions générales
            </a>
            <a href="/" target="_blank"  rel="noopener noreferrer"
              className="rounded-full border border-secondary-dark px-5 py-2 text-dark transition hover:bg-secondary-dark hover:text-light">
              Notice d'information
            </a>
          </div>
        </div>

        {/* Colonne droite : Récapitulatif */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">
            Récapitulatif du paiement
          </h2>

          <div className="space-y-4">
            <div>
              {principalIsOver65 ? (
                <p className="text-sm text-amber-700 font-medium italic">
                  Montant en attente d'étude médicale
                </p>
              ) : (
                <>
                  <p className="text-sm text-secondary font-medium">Montant total</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatCurrency(price)} F CFA
                  </p>
                </>
              )}
            </div>

            {/* 1. Assurance */}
            <div className="mb-6">
              <div className="bg-secondary text-white p-4 rounded-t-lg font-bold">
                Assurance
              </div>
              <div className="border border-gray-300">
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
             
                    <tbody>
                      <tr className="bg-white">
                        <td className="border border-gray-300 p-3 font-medium">Formule</td>
                        <td className="border border-gray-300 p-3">
                          {packageName || selectedPackage || "Non sélectionnée"}
                        </td>
                      </tr>
                      <tr className="bg-gray-50">
                        <td className="border border-gray-300 p-3 font-medium">Périodicité</td>
                        <td className="border border-gray-300 p-3">
                          {periodicity === "annuelle"
                            ? "Annuelle"
                            : periodicity === "mensuelle"
                              ? "Mensuelle"
                              : periodicity === "trimestriel"
                                ? "Trimestrielle"
                                : periodicity || "Non définie"}
                        </td>
                      </tr>
                      <tr className="bg-white">
                        <td className="border border-gray-300 p-3 font-medium">Date d'effet</td>
                        <td className="border border-gray-300 p-3">
                          {effectiveDate
                            ? new Date(effectiveDate).toLocaleDateString("fr-FR")
                            : "Non définie"}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* 2. Souscripteur */}
            {subscriberData && (
              <div className="mb-6">
                <div className="bg-secondary text-white p-4 rounded-t-lg font-bold">
                  Souscripteur
                </div>
                <div className="border border-gray-300">
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      
                      <tbody>
                        <tr className="bg-white">
                          <td className="border border-gray-300 p-3 font-medium">Nom</td>
                          <td className="border border-gray-300 p-3">{subscriberData.nom || "-"}</td>
                        </tr>
                        <tr className="bg-gray-50">
                          <td className="border border-gray-300 p-3 font-medium">Prénom(s)</td>
                          <td className="border border-gray-300 p-3">{subscriberData.prenoms || "-"}</td>
                        </tr>
                        <tr className="bg-white">
                          <td className="border border-gray-300 p-3 font-medium">Sexe</td>
                          <td className="border border-gray-300 p-3">
                            {subscriberData.sexe === "M"
                              ? "Masculin"
                              : subscriberData.sexe === "F"
                                ? "Féminin"
                                : "-"}
                          </td>
                        </tr>
                        <tr className="bg-gray-50">
                          <td className="border border-gray-300 p-3 font-medium">Email</td>
                          <td className="border border-gray-300 p-3">{subscriberData.email || "-"}</td>
                        </tr>
                        <tr className="bg-white">
                          <td className="border border-gray-300 p-3 font-medium">Téléphone</td>
                          <td className="border border-gray-300 p-3">{subscriberData.telephone || "-"}</td>
                        </tr>
                        <tr className="bg-gray-50">
                          <td className="border border-gray-300 p-3 font-medium">Date de naissance</td>
                          <td className="border border-gray-300 p-3">
                            {subscriberData.date_naissance
                              ? new Date(
                                  subscriberData.date_naissance,
                                ).toLocaleDateString("fr-FR")
                              : "-"}
                          </td>
                        </tr>
                        <tr className="bg-white">
                          <td className="border border-gray-300 p-3 font-medium">Type de pièce</td>
                          <td className="border border-gray-300 p-3">{subscriberData.type_piece || "-"}</td>
                        </tr>
                        <tr className="bg-white">
                          <td className="border border-gray-300 p-3 font-medium">N° Pièce</td>
                          <td className="border border-gray-300 p-3">{(subscriberData.numero_CNI ?? subscriberData.numero_piece) || "-"}</td>
                        </tr>
                        {/* <tr className="bg-gray-50">
                          <td className="border border-gray-300 p-3 font-medium">N° NNI</td>
                          <td className="border border-gray-300 p-3">{subscriberData.nni || "-"}</td>
                        </tr> */}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* 3. Assuré Principal */}
            {isPrincipal === "yes" && subscriberData && (
              <div className="mb-6">
                <div className="bg-secondary text-white p-4 rounded-t-lg font-bold">
                  Assuré Principal: {subscriberData?.prenoms} {subscriberData?.nom}
                </div>
                <div className="border border-gray-300">
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                     
                      <tbody>
                        <tr className="bg-white">
                          <td className="border border-gray-300 p-3 font-medium">Sexe</td>
                          <td className="border border-gray-300 p-3">
                            {subscriberData.sexe === "M"
                              ? "Masculin"
                              : subscriberData.sexe === "F"
                                ? "Féminin"
                                : "-"}
                          </td>
                        </tr>
                        <tr className="bg-gray-50">
                          <td className="border border-gray-300 p-3 font-medium">Email</td>
                          <td className="border border-gray-300 p-3">{subscriberData.email || "-"}</td>
                        </tr>
                        <tr className="bg-white">
                          <td className="border border-gray-300 p-3 font-medium">Téléphone</td>
                          <td className="border border-gray-300 p-3">{subscriberData.telephone || "-"}</td>
                        </tr>
                        <tr className="bg-gray-50">
                          <td className="border border-gray-300 p-3 font-medium">Date de naissance</td>
                          <td className="border border-gray-300 p-3">
                            {subscriberData.date_naissance
                              ? new Date(
                                  subscriberData.date_naissance,
                                ).toLocaleDateString("fr-FR")
                              : "-"}
                          </td>
                        </tr>
                        <tr className="bg-white">
                          <td className="border border-gray-300 p-3 font-medium">Type de pièce</td>
                          <td className="border border-gray-300 p-3">{subscriberData.type_piece || "-"}</td>
                        </tr>
                        <tr className="bg-white">
                          <td className="border border-gray-300 p-3 font-medium">N° Pièce</td>
                          <td className="border border-gray-300 p-3">{(subscriberData.numero_CNI ?? subscriberData.numero_piece) || "-"}</td>
                        </tr>
                        {/* <tr className="bg-gray-50">
                          <td className="border border-gray-300 p-3 font-medium">N° NNI</td>
                          <td className="border border-gray-300 p-3">{subscriberData.nni || "-"}</td>
                        </tr> */}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {isPrincipal === "no" && principalData && (
              <div className="mb-6">
                <div className="bg-secondary text-white p-4 rounded-t-lg font-bold">
                  Assuré Principal: {principalData?.prenoms} {principalData?.nom}
                </div>
                <div className="border border-gray-300">
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                     
                      <tbody>
                        <tr className="bg-white">
                          <td className="border border-gray-300 p-3 font-medium">Sexe</td>
                          <td className="border border-gray-300 p-3">
                            {principalData.sexe === "M"
                              ? "Masculin"
                              : principalData.sexe === "F"
                                ? "Féminin"
                                : "-"}
                          </td>
                        </tr>
                        <tr className="bg-gray-50">
                          <td className="border border-gray-300 p-3 font-medium">Email</td>
                          <td className="border border-gray-300 p-3">{principalData.email || "-"}</td>
                        </tr>
                        <tr className="bg-white">
                          <td className="border border-gray-300 p-3 font-medium">Téléphone</td>
                          <td className="border border-gray-300 p-3">{principalData.telephone || "-"}</td>
                        </tr>
                        <tr className="bg-gray-50">
                          <td className="border border-gray-300 p-3 font-medium">Date de naissance</td>
                          <td className="border border-gray-300 p-3">
                            {principalData.date_naissance
                              ? new Date(
                                  principalData.date_naissance,
                                ).toLocaleDateString("fr-FR")
                              : "-"}
                          </td>
                        </tr>
                        <tr className="bg-white">
                          <td className="border border-gray-300 p-3 font-medium">Type de pièce</td>
                          <td className="border border-gray-300 p-3">{principalData.type_pièce || "-"}</td>
                        </tr>
                        <tr className="bg-white">
                          <td className="border border-gray-300 p-3 font-medium">N° Pièce</td>
                          <td className="border border-gray-300 p-3">{(principalData.numero_CNI ?? principalData.numero_piece) || "-"}</td>
                        </tr>
                        {/* <tr className="bg-gray-50">
                          <td className="border border-gray-300 p-3 font-medium">N° NNI</td>
                          <td className="border border-gray-300 p-3">{principalData.nni || "-"}</td>
                        </tr> */}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* 4. Conjoint */}
            {spouse && spouse.nom && (
              <div className="mb-6">
                <div className="bg-secondary text-white p-4 rounded-t-lg font-bold">
                  Conjoint(e): {spouse.prenoms} {spouse.nom}
                </div>
                <div className="border border-gray-300">
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                     
                      <tbody>
                        <tr className="bg-white">
                          <td className="border border-gray-300 p-3 font-medium">Nom</td>
                          <td className="border border-gray-300 p-3">{spouse.nom || "-"}</td>
                        </tr>
                        <tr className="bg-gray-50">
                          <td className="border border-gray-300 p-3 font-medium">Prénom(s)</td>
                          <td className="border border-gray-300 p-3">{spouse.prenoms || "-"}</td>
                        </tr>
                        <tr className="bg-white">
                          <td className="border border-gray-300 p-3 font-medium">Sexe</td>
                          <td className="border border-gray-300 p-3">
                            {spouse.sexe === "M"
                              ? "Masculin"
                              : spouse.sexe === "F"
                                ? "Féminin"
                                : "-"}
                          </td>
                        </tr>
                        <tr className="bg-gray-50">
                          <td className="border border-gray-300 p-3 font-medium">Date de naissance</td>
                          <td className="border border-gray-300 p-3">
                            {spouse.dob
                              ? new Date(spouse.dob).toLocaleDateString("fr-FR")
                              : "-"}
                          </td>
                        </tr>
                        {/* <tr className="bg-white">
                          <td className="border border-gray-300 p-3 font-medium">N° NNI</td>
                          <td className="border border-gray-300 p-3">{spouse.nni || "-"}</td>
                        </tr> */}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* 5. Enfants */}
            {children && children.length > 0 && (
              <div className="mb-6">
                {children.map((child, index) => (
                  <div key={index} className="mb-6">
                    <div className="bg-secondary text-white p-4 rounded-t-lg font-bold">
                      Enfant {index + 1}: {child.prenoms} {child.nom}
                    </div>
                    <div className="border border-gray-300">
                      <div className="overflow-x-auto">
                        <table className="w-full border-collapse">
                          
                          <tbody>
                            <tr className="bg-white">
                              <td className="border border-gray-300 p-3 font-medium">Nom</td>
                              <td className="border border-gray-300 p-3">{child.nom || "-"}</td>
                            </tr>
                            <tr className="bg-gray-50">
                              <td className="border border-gray-300 p-3 font-medium">Prénom(s)</td>
                              <td className="border border-gray-300 p-3">{child.prenoms || "-"}</td>
                            </tr>
                            <tr className="bg-white">
                              <td className="border border-gray-300 p-3 font-medium">Sexe</td>
                              <td className="border border-gray-300 p-3">
                                {child.sexe === "M"
                                  ? "Masculin"
                                  : child.sexe === "F"
                                    ? "Féminin"
                                    : "-"}
                              </td>
                            </tr>
                            <tr className="bg-gray-50">
                              <td className="border border-gray-300 p-3 font-medium">Date de naissance</td>
                              <td className="border border-gray-300 p-3">
                                {child.dob
                                  ? new Date(child.dob).toLocaleDateString("fr-FR")
                                  : "-"}
                              </td>
                            </tr>
                            {/* <tr className="bg-white">
                              <td className="border border-gray-300 p-3 font-medium">N° NNI</td>
                              <td className="border border-gray-300 p-3">{child.nni || "-"}</td>
                            </tr> */}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
